import { useCallback, useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import adminApi, { apiError } from "@/lib/adminApi";
import { EmptyState, Panel, Skeleton, StatusBadge, T, btnPrimary, btnSecondary, inputCls, DeleteButton } from "@/components/admin/ui";

const STATUS_LABELS = { available: "Disponible", on_trip: "En trajet", unavailable: "Indisponible", maintenance: "Maintenance" };
const DOC_TYPES = ["assurance", "immatriculation", "contrôle technique", "expertise"];

export default function AdminVehicles() {
  const [vehicles, setVehicles] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [drawer, setDrawer] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const empty = { make: "", model: "", year: new Date().getFullYear(), plate: "", color: "", category: "business", passengers: 3, luggage: 3, owner: "", usual_driver_id: "", status: "available", documents: [], notes: "" };
  const [form, setForm] = useState(empty);

  const load = useCallback(() => {
    adminApi.get("/vehicles").then((r) => setVehicles(r.data.vehicles)).catch(() => {});
    adminApi.get("/drivers").then((r) => setDrivers(r.data.drivers)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const openEdit = (v) => {
    setEditing(v.id);
    setForm({ ...empty, ...v, usual_driver_id: v.usual_driver_id || "", owner: v.owner || "", color: v.color || "", notes: v.notes || "" });
    setDrawer(true);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const payload = { ...form, year: Number(form.year) || null, passengers: Number(form.passengers), luggage: Number(form.luggage), usual_driver_id: form.usual_driver_id || null, owner: form.owner || null };
      if (editing) await adminApi.patch(`/vehicles/${editing}`, payload);
      else await adminApi.post("/vehicles", payload);
      toast.success(editing ? "Véhicule mis à jour" : "Véhicule créé");
      setDrawer(false); setEditing(null); setForm(empty);
      load();
    } catch (error) {
      toast.error(apiError(error, "Erreur"));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-vehicles">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={T.pageTitle}>Véhicules</h1>
          <p className="mt-1 text-sm text-slate">Inventaire physique de la flotte.</p>
        </div>
        <button type="button" onClick={() => { setEditing(null); setForm(empty); setDrawer(true); }} className={btnPrimary} data-testid="vehicles-new-button">
          <Plus className="h-4 w-4" strokeWidth={2} /> Véhicule
        </button>
      </div>

      <Panel testId="vehicles-list">
        {!vehicles && <Skeleton rows={3} />}
        {vehicles?.length === 0 && (
          <EmptyState title="Aucun véhicule" hint="Enregistrez le premier véhicule de la flotte." testId="vehicles-empty"
            action={<button type="button" onClick={() => { setEditing(null); setForm(empty); setDrawer(true); }} className="text-sm font-semibold text-miles hover:underline">Ajouter un véhicule</button>} />
        )}
        <div className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
          {vehicles?.map((v) => (
            <button key={v.id} type="button" onClick={() => openEdit(v)} data-testid={`vehicle-card-${v.plate}`}
              className="rounded-xl bg-ink/[0.025] p-4 text-left transition-colors hover:bg-miles-ice/50">
              <div className="flex items-center justify-between">
                <p className="text-[15px] font-semibold text-ink">{v.make} {v.model}</p>
                <StatusBadge status={v.status} />
              </div>
              <p className="mt-1 font-mono text-[13px] text-slate">{v.plate} · {v.color || "—"}</p>
              <div className="mt-3 flex justify-between font-mono text-xs text-slate">
                <span>{v.category}</span>
                <span>{v.rides_total} trajet(s)</span>
                <span className="text-ink">{v.usual_driver ? `${v.usual_driver.first_name} ${v.usual_driver.last_name}` : ""}</span>
              </div>
            </button>
          ))}
        </div>
      </Panel>

      {drawer && (
        <div className="fixed inset-0 z-50" data-testid="vehicle-drawer">
          <div className="absolute inset-0 bg-ink/50" onClick={() => setDrawer(false)} />
          <div className="absolute inset-y-0 right-0 w-full max-w-md overflow-auto bg-white p-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-ink">{editing ? "Modifier le véhicule" : "Nouveau véhicule"}</h2>
              <button type="button" onClick={() => setDrawer(false)} aria-label="Fermer"><X className="h-5 w-5 text-slate" strokeWidth={1.5} /></button>
            </div>
            <form onSubmit={submit} className="space-y-3" data-testid="vehicle-form">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-medium text-slate">Marque *</label>
                  <input required value={form.make} onChange={set("make")} data-testid="vehicle-make" className={`${inputCls} mt-1`} placeholder="Mercedes-Benz" /></div>
                <div><label className="text-xs font-medium text-slate">Modèle *</label>
                  <input required value={form.model} onChange={set("model")} data-testid="vehicle-model" className={`${inputCls} mt-1`} placeholder="EQE 350" /></div>
                <div><label className="text-xs font-medium text-slate">Plaque *</label>
                  <input required value={form.plate} onChange={set("plate")} data-testid="vehicle-plate" className={`${inputCls} mt-1 font-mono`} placeholder="GE 000 000" /></div>
                <div><label className="text-xs font-medium text-slate">Année</label>
                  <input type="number" value={form.year} onChange={set("year")} data-testid="vehicle-year" className={`${inputCls} mt-1 font-mono`} /></div>
                <div><label className="text-xs font-medium text-slate">Couleur</label>
                  <input value={form.color || ""} onChange={set("color")} data-testid="vehicle-color" className={`${inputCls} mt-1`} /></div>
                <div><label className="text-xs font-medium text-slate">Catégorie</label>
                  <select value={form.category} onChange={set("category")} data-testid="vehicle-category" className={`${inputCls} mt-1`}>
                    <option value="business">Business</option><option value="first">First</option><option value="van">Van</option><option value="standard">Standard</option>
                  </select></div>
                <div><label className="text-xs font-medium text-slate">Chauffeur habituel</label>
                  <select value={form.usual_driver_id || ""} onChange={set("usual_driver_id")} data-testid="vehicle-driver" className={`${inputCls} mt-1`}>
                    <option value="">—</option>
                    {drivers.map((d) => <option key={d.id} value={d.id}>{d.first_name} {d.last_name}</option>)}
                  </select></div>
                <div><label className="text-xs font-medium text-slate">Statut</label>
                  <select value={form.status} onChange={set("status")} data-testid="vehicle-status" className={`${inputCls} mt-1`}>
                    {Object.entries(STATUS_LABELS).map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                  </select></div>
              </div>
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate">Documents</p>
                {(form.documents || []).map((doc, i) => (
                  <div key={i} className="mb-2 flex items-center gap-2">
                    <select value={doc.type} className={`${inputCls} w-44`}
                      onChange={(e) => setForm((f) => { const docs = [...f.documents]; docs[i] = { ...docs[i], type: e.target.value }; return { ...f, documents: docs }; })}>
                      {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <input type="date" value={doc.expires_at || ""} className={inputCls}
                      onChange={(e) => setForm((f) => { const docs = [...f.documents]; docs[i] = { ...docs[i], expires_at: e.target.value }; return { ...f, documents: docs }; })} />
                    <button type="button" aria-label="Supprimer" onClick={() => setForm((f) => ({ ...f, documents: f.documents.filter((_, j) => j !== i) }))}>
                      <X className="h-4 w-4 text-slate" strokeWidth={1.5} />
                    </button>
                  </div>
                ))}
                <button type="button" className={btnSecondary} data-testid="vehicle-doc-add"
                  onClick={() => setForm((f) => ({ ...f, documents: [...(f.documents || []), { type: "assurance", expires_at: "" }] }))}>
                  <Plus className="h-4 w-4" strokeWidth={2} /> Document
                </button>
              </div>
              <div className="flex gap-2 pt-2">
                <button type="submit" disabled={busy} className={btnPrimary} data-testid="vehicle-save">{busy ? "Enregistrement…" : "Enregistrer"}</button>
                <button type="button" onClick={() => setDrawer(false)} className={btnSecondary}>Annuler</button>
              </div>
              {editing && (
                <div className="mt-4 border-t border-ink/[0.07] pt-4" onClick={(e) => e.stopPropagation()}>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-red-500/80">Zone sensible</p>
                  <DeleteButton testId="vehicle-delete" label="Supprimer ce véhicule"
                    onConfirm={() => adminApi.delete(`/vehicles/${editing}`)}
                    onDeleted={() => { setDrawer(false); load(); }} />
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
